import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

// Transaction types matching backend response
interface CommTransaction {
  id: string;
  time: string;
  from: string;
  to: string;
  status: string;
  count: number;
  cost: number;
}

interface ListScrubberTransaction {
  id: string;
  time: string;
  batch_name: string;
  file_name: string;
  contacts: number;
  cost: number;
}

interface PhoneLineTransaction {
  id: string;
  time: string;
  phone_number: string;
  description: string;
  cost: number;
}

interface PlanChargeTransaction {
  id: string;
  time: string;
  plan_name: string;
  description: string;
  cost: number;
}

interface TransferOutTransaction {
  id: string;
  time: string;
  recipient: string;
  description: string;
  cost: number;
}

type Transaction = CommTransaction | ListScrubberTransaction | PhoneLineTransaction | PlanChargeTransaction | TransferOutTransaction;

interface UsageTransactionsProps {
  locationId: string;
  category: string;
  date: string;
  onBack: () => void;
  onBackToSummary: () => void;
}

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

// Status badge styles for communications
const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  delivered: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' },
  sent: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' },
  queued: { bg: 'rgba(251, 191, 36, 0.15)', text: '#d97706' },
  pending: { bg: 'rgba(251, 191, 36, 0.15)', text: '#d97706' },
  failed: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444' },
  undelivered: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444' },
  unknown: { bg: 'rgba(156, 163, 175, 0.15)', text: '#6b7280' },
};

/**
 * Individual Transactions View (Level 3)
 * Shows individual transaction records for a specific category and date
 */
export function UsageTransactions({
  locationId,
  category,
  date,
  onBack,
  onBackToSummary,
}: UsageTransactionsProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [unit, setUnit] = useState('');
  const [columnType, setColumnType] = useState<'communication' | 'list_scrubber' | 'phone_line' | 'plan_charge' | 'transfer_out' | 'a2p_registration_fee'>('communication');
  const [totalCount, setTotalCount] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [retentionLimited, setRetentionLimited] = useState(false);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);

  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/transactions?category=${category}&date=${date}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as {
        success: boolean;
        displayName?: string;
        unit?: string;
        columnType?: 'communication' | 'list_scrubber' | 'phone_line' | 'plan_charge' | 'transfer_out' | 'a2p_registration_fee';
        transactions?: Transaction[];
        total_count?: number;
        total_cost?: number;
        retention_limited?: boolean;
        retention_days?: number;
        error?: string;
      };
      if (data.success) {
        setTransactions(data.transactions || []);
        setDisplayName(data.displayName || category);
        setUnit(data.unit || 'items');
        setColumnType(data.columnType || 'communication');
        setTotalCount(data.total_count || 0);
        setTotalCost(data.total_cost || 0);
        setRetentionLimited(Boolean(data.retention_limited));
        setRetentionDays(data.retention_days ?? null);
      } else {
        setError(data.error || 'Failed to load transactions');
      }
    } catch (err) {
      console.error('[UsageTransactions] Fetch error:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [locationId, category, date]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Reset to page 1 when data or rowsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [transactions.length, rowsPerPage]);

  // Pagination calculations
  const totalPages = Math.ceil(transactions.length / rowsPerPage);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return transactions.slice(startIndex, startIndex + rowsPerPage);
  }, [transactions, currentPage, rowsPerPage]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    const d = new Date(timeStr);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '-';
    // Format as +1 (XXX) XXX-XXXX if it's a US number
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  const formatCount = (count: number) => {
    return count.toLocaleString();
  };

  const getStatusStyle = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    return STATUS_STYLES[normalizedStatus] || STATUS_STYLES.unknown;
  };

  const capitalizeStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Get column header for count column based on category
  const getCountHeader = () => {
    if (category.startsWith('voice_')) return 'Duration';
    if (category.startsWith('sms_') || category.startsWith('mms_')) return 'Segments';
    return 'Count';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchTransactions}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Title and Breadcrumb */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">Individual Transactions</h2>
        <nav className="text-sm">
          <button
            onClick={onBackToSummary}
            className="text-blue-600 hover:underline"
          >
            Usage Summary
          </button>
          <span className="mx-2 text-gray-400">&rsaquo;</span>
          <button
            onClick={onBack}
            className="text-blue-600 hover:underline"
          >
            {displayName}
          </button>
          <span className="mx-2 text-gray-400">&rsaquo;</span>
          <span className="font-semibold text-gray-900">{formatDate(date)}</span>
        </nav>
      </div>

      {/* Context Header (Blue Box) */}
      <div className="bg-sky-50 border-2 border-sky-500 rounded-lg p-4 mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-base font-bold text-sky-700">
            {displayName} - {formatDate(date)}
          </h3>
          <p className="text-sm text-gray-600">
            {formatCount(totalCount)} {unit} = {formatCurrency(totalCost)}
          </p>
        </div>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-gray-50 rounded text-sm font-medium hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Daily
        </button>
      </div>

      {/* Controls Row */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ROWS_PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="text-sm font-semibold text-gray-700">rows</span>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            {columnType === 'communication' && (
              <tr>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Time
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  From
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  To
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Status
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  {getCountHeader()}
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Cost
                </th>
              </tr>
            )}
            {columnType === 'list_scrubber' && (
              <tr>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Time
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Batch Name
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  File Name
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Contacts
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Cost
                </th>
              </tr>
            )}
            {columnType === 'phone_line' && (
              <tr>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Time
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Phone Number
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Description
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Cost
                </th>
              </tr>
            )}
            {columnType === 'plan_charge' && (
              <tr>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Time
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Plan Name
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Description
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Cost
                </th>
              </tr>
            )}
            {columnType === 'transfer_out' && (
              <tr>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Time
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Recipient
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Description
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Amount
                </th>
              </tr>
            )}
            {columnType === 'a2p_registration_fee' && (
              <tr>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Time
                </th>
                <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Description
                </th>
                <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                  Cost
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {retentionLimited ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-amber-800 bg-amber-50">
                  Per-message detail is retained for the last {retentionDays ?? 45} days only.
                  Daily totals for older periods remain available in the breakdown view above.
                </td>
              </tr>
            ) : paginatedTransactions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-gray-500">
                  No transactions for this date
                </td>
              </tr>
            ) : columnType === 'communication' ? (
              paginatedTransactions.map((tx) => {
                const commTx = tx as CommTransaction;
                const statusStyle = getStatusStyle(commTx.status);
                return (
                  <tr key={commTx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(commTx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatPhoneNumber(commTx.from)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatPhoneNumber(commTx.to)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm">
                      <span
                        className="px-2.5 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.text,
                        }}
                      >
                        {capitalizeStatus(commTx.status)}
                      </span>
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-gray-900">
                      {commTx.count}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-orange-600">
                      {formatCurrency(commTx.cost)}
                    </td>
                  </tr>
                );
              })
            ) : columnType === 'list_scrubber' ? (
              paginatedTransactions.map((tx) => {
                const scrubTx = tx as ListScrubberTransaction;
                return (
                  <tr key={scrubTx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(scrubTx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {scrubTx.batch_name}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {scrubTx.file_name || '-'}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-gray-900">
                      {formatCount(scrubTx.contacts)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-orange-600">
                      {formatCurrency(scrubTx.cost)}
                    </td>
                  </tr>
                );
              })
            ) : columnType === 'phone_line' ? (
              paginatedTransactions.map((tx) => {
                const phoneTx = tx as PhoneLineTransaction;
                return (
                  <tr key={phoneTx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(phoneTx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatPhoneNumber(phoneTx.phone_number) || '-'}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {phoneTx.description}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-orange-600">
                      {formatCurrency(phoneTx.cost)}
                    </td>
                  </tr>
                );
              })
            ) : columnType === 'plan_charge' ? (
              paginatedTransactions.map((tx) => {
                const planTx = tx as PlanChargeTransaction;
                return (
                  <tr key={planTx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(planTx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {planTx.plan_name}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {planTx.description}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-orange-600">
                      {formatCurrency(planTx.cost)}
                    </td>
                  </tr>
                );
              })
            ) : columnType === 'transfer_out' ? (
              paginatedTransactions.map((tx) => {
                const transferTx = tx as TransferOutTransaction;
                return (
                  <tr key={transferTx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(transferTx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {transferTx.recipient}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {transferTx.description}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-orange-600">
                      {formatCurrency(transferTx.cost)}
                    </td>
                  </tr>
                );
              })
            ) : columnType === 'a2p_registration_fee' ? (
              paginatedTransactions.map((tx) => {
                // Same shape as plan_charge / transfer_out — time + description + cost
                const a2pTx = tx as { id: string; time: string; description: string; cost: number };
                return (
                  <tr key={a2pTx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(a2pTx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {a2pTx.description}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-orange-600">
                      {formatCurrency(a2pTx.cost)}
                    </td>
                  </tr>
                );
              })
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {transactions.length > 0 && (
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {Math.min((currentPage - 1) * rowsPerPage + 1, transactions.length)} -{' '}
            {Math.min(currentPage * rowsPerPage, transactions.length)} of {transactions.length} transactions
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-3 py-1.5 text-sm text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  className={`px-3 py-1.5 border rounded text-sm font-medium ${
                    currentPage === page
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
